import type { Prompt } from "@1js/pl-card-components";
import { useMsal } from "@azure/msal-react";
import * as React from "react";
import { Handoff, type HandoffRef } from "../../components/Handoff/Handoff.web";
import { PromptDetailDiv } from "../../components/PromptDetail/PromptDetailDiv.web";
import type { WebPrompt } from "../../components/data";
import { useFeatureEnablement } from "../../hooks/useFeatureEnablement";
import { useFlightContext } from "../../hooks/useFlightContext";
import { useSharePrompt } from "../../hooks/useSharePrompt";
import {
  addFavoriteAsync,
  fetchBookmarkedPrompts,
  removeFavoriteAsync,
} from "../../services/prompt-client.web";
import useTelemetry from "../../utils/Telemetry/useTelemetry";
import { MetaOSAppContext } from "../metaos/MetaOSAppContext";
import { useStyles } from "./DetailPageContent.web.styles";
import useDetailPrompt from "./useDetailPrompt";

export type DetailPageContentProps = {
  serviceHost: string;
  locale: string;
  prompt: WebPrompt;
  moreLikeThis: WebPrompt[];
};

export const DetailPageContent: React.FC<DetailPageContentProps> = (props) => {
  const telemetry = useTelemetry();
  const styles = useStyles();
  const { state, dispatch } = useDetailPrompt(props.prompt);
  const sharePrompt = useSharePrompt();
  const { isTryInOnAuthed } = useFeatureEnablement();
  const { isFlightActive, setFlightState } = useFlightContext();

  const metaOSAppContext = React.useContext(MetaOSAppContext);
  const msalInstance = useMsal();
  const instance =
    metaOSAppContext.isMosApp && metaOSAppContext.pca
      ? msalInstance.instance
      : undefined;

  const fetchPromptsAsync = React.useCallback(async () => {
    if (!state.init && isFlightActive("isBookmarksEnabled")) {
      try {
        dispatch({ type: "REQUEST_BOOKMARKED_PROMPTS_INIT" });
        const prompts: Prompt[] = await fetchBookmarkedPrompts(
          props.serviceHost,
          props.locale,
          telemetry.sendApiActionEvent,
          instance
        );
        dispatch({
          type: "REQUEST_BOOKMARKED_PROMPTS_SUCCEED",
          payload: {
            prompts,
          },
        });
      } catch (error) {
        dispatch({
          type: "REQUEST_BOOKMARKED_PROMPTS_FAILED",
        });
        setFlightState("isBookmarksEnabled", false);
      }
    }
  }, [
    state.init,
    isFlightActive,
    dispatch,
    props.serviceHost,
    props.locale,
    telemetry.sendApiActionEvent,
    instance,
    setFlightState,
  ]);

  React.useEffect(() => {
    fetchPromptsAsync();
  }, [fetchPromptsAsync]);

  const handleMoreLikeThisClick = React.useCallback(
    async (prompt: Prompt) => {
      telemetry.sendMoreLikeThisPromptClickEvent(prompt);
      window.location.href = sharePrompt.buildPromptDetailPageUrlWithLocale(
        prompt as WebPrompt
      );
    },
    [telemetry, sharePrompt]
  );

  const handleOnCopyClicked = React.useCallback(
    async (prompt: Prompt) => {
      telemetry.sendCopyPromptEvent(prompt);
    },
    [telemetry]
  );

  const handleOnBookmarked = React.useCallback(
    async (prompt: Prompt) => {
      try {
        dispatch({ type: "BOOKMARK_INIT" });
        if (prompt.UserActivity.Favorite.IsFavorite) {
          await removeFavoriteAsync(
            props.serviceHost,
            prompt,
            telemetry.sendApiActionEvent,
            instance
          );
        } else {
          await addFavoriteAsync(
            props.serviceHost,
            prompt,
            telemetry.sendApiActionEvent,
            instance
          );
        }

        dispatch({
          type: "BOOKMARK_SUCCEED",
          payload: { value: !prompt.UserActivity.Favorite.IsFavorite },
        });
      } catch (error) {
        console.error("Error fetching more prompts:", error);
        dispatch({ type: "BOOKMARK_FAILED" });
        throw error;
      }
      telemetry.sendPromptBookmarkedEvent(prompt);
    },

    [dispatch, instance, props.serviceHost, telemetry]
  );

  const handoffRef = React.useRef<HandoffRef>(null);

  return (
    <>
      {isTryInOnAuthed && (
        <Handoff
          serviceHost={props.serviceHost}
          locale={props.locale}
          ref={handoffRef}
        />
      )}
      <div className={styles.main}>
        <PromptDetailDiv
          isBookmarkEnabled={isFlightActive("isBookmarksEnabled")}
          prompt={state.prompt}
          morePrompts={props.moreLikeThis}
          onMoreLikeThisClicked={handleMoreLikeThisClick}
          onCopyClicked={handleOnCopyClicked}
          onBookmarkToggled={handleOnBookmarked}
          onTryInAppBadgeAction={handoffRef.current?.onTryInAppBadgeAction}
        />
      </div>
    </>
  );
};
